// BUILD-046 tasks 8.16 / 8.22 / 8.23 / 8.24, rebuilt in correction cycle 1bb88b3e (stage B2a, items F2 on the screen, N1 and N9): "Requests I issued".
//
// A clearly labelled SUBVIEW of My Work (reached from the toolbar), not a panel under the shared queue: requested work is the Caller's own requests
// to others, never mixed into personal assignments or the queue. The entries come from the SERVER (`GET /work/issued`, issuedRequestsModel.ts), so they
// survive a reload; Load more reads the next page; an older server that does not serve the route says "Requests I issued are unavailable on this server".
// An empty list is ONE short line, not an explanatory panel.
//
// Each entry is the same compact row as every other tab (TaskRow).
//  - An "Everyone must complete" request shows the server's "N of M", the readable recipients, and the issuer actions of task 8.23 - Add recipient, Cancel
//    request (and Remove for one recipient) - to the requester, the company holder and an established direct manager only (issuerActionsModel.ts). A removal
//    or a cancellation is an ending, shown apart from completion and never counted in N; a refusal (a source-governed requirement, an ineligible person, a
//    duplicate) shows the server's message and changes nothing. Add recipient and Cancel request are entries of the row's "More actions" menu.
//  - An "Anyone can complete" request to a named group shows the live-queue wording while it waits, who took it, and - when the owner has left the group -
//    the server's owner-left text exactly as given (task 8.24), with Reassign… / Return to queue in the same menu for authorized actors only.
// A figure that cannot be read is "Unavailable" with a Retry, never "0 of 0".
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { notifySharedTasksChanged, onCallerChanged, workApi } from '../../api/workShared';
import type { IssuedRow, IssuedStore, IssuedTask, IssuedView } from './issuedRequestsModel';
import {
  askCancel,
  askRemove,
  callerChangedIssuer,
  closeIssuer,
  describeIssuerFlow,
  dismissIssuerOutcome,
  initialIssuerFlow,
  isLiveAssignment,
  issuerControls,
  loadRecipients,
  openAdd,
  performIssuerAction,
  selectRecipient,
} from './issuerActionsModel';
import type { IssuerFlowState, IssuerMe, IssuerStore } from './issuerActionsModel';
import { TaskActionControls } from './TaskActionControls';
import { TaskHistoryPanel } from './TaskHistoryPanel';
// Section A (Owner direction of 2026-10-21): Edit task, Hand off and History, offered by the same rules as Company / Team and My Tasks (taskActionOffers.ts).
import { EDIT_EVERYONE_HINT, editBlockedHint, taskEntries, taskEntryOffers } from './taskActionOffers';
import type { TaskEntryKind } from './taskActionOffers';
import type { ActionMe } from './oversightActionsModel';
import { RowMoreMenu } from './RowMoreMenu';
import type { MenuEntry } from './RowMoreMenu';
import { ActionsCell, Cell, QuietLine, TaskCell, TaskTable, TaskTableRow } from './TaskTable';
import { formatDueDate } from './taskRowModel';
import { associationParts } from './CustomerParts';
import { useCustomersInPlay } from './useCustomerColumn';
import type { Density } from './densityStore';
// D1 display cleanup: the request title and group names are shown without the "(FICTIONAL)" test marker; people are First name + last initial (full names on hover and focus).
import { disambiguatePeople, fullName } from './displayNames';
import { PersonName } from './PersonName';
import { usePhoenixDay } from './usePhoenixDay';
import { ISSUED_STATUS_OPTIONS, statusLabelOf } from './statusChoiceModel';
import type { IssuedStatusChoice } from './statusChoiceModel';
import { issuedFollowUpNote, matchingMemberNames } from './issuedStatusModel';
import type { IssuedFollowUpNote } from './issuedStatusModel';
// Increment 1b-i: the Assigned to cell shows initials circles; the expansion is wide, so each recipient there is a decorative circle AND the name.
import { AvatarCircle } from './PersonAvatar';
import { avatarLabels } from './personAvatarModel';
// Increment 1a: the columns Task | Assigned to | Status / progress | Priority | Due | Assigned / age | Actions. What each says is issuedRowModel.ts's; the cells are IssuedRowCells.tsx's.
import { describeIssuedColumns, recipientsDetailOf } from './issuedRowModel';
import { AssignedAgeView, DueView, ModeBadge, PriorityView, ProgressView, RecipientsToggle, ResponsibleView } from './IssuedRowCells';
// Owner correction 2026-10-24 (item 2): what the requester reads of the work on a task they issued (latest progress with its author, next action, follow-up, recorded due-date changes), and (supplement)
// the Reassign / Add recipients requests the Edit form makes of the row that owns them.
import { DueChangedButton, DueChangesPanel, InsightLines, MemberInsight, hasInsightLines } from './RequesterInsightView';
import { useAssignmentRequest } from './assignmentActionStore';
// Increment 1b-ii: with an "Assigned to" filter on, the expansion of an Everyone request that matched marks the matched recipient in words ("Matches filter"). Without the filter nobody is marked.
import { ANY_ASSIGNEE, ASSIGNEE_MATCH_MARKER, isAssigneeMatch } from './assigneeFilterModel';
import type { AssigneeFilter } from './assigneeFilterModel';

type IssuedMe = IssuerMe & ActionMe;

export interface IssuedRequestsPanelProps {
  issued: IssuedStore;
  /** What the store shows (describeIssued of its state): status, messages, paging facts. */
  view: IssuedView;
  /** The rows to draw: `view.rows` after the host's search. */
  rows: readonly IssuedRow[];
  /** True while a search or the "Assigned to" filter narrows the list (so an empty result says so). */
  narrowed: boolean;
  /** The "Assigned to" filter now (increment 1b-ii). Absent means no filter. It only marks the matched recipient in the expansion of an Everyone request; the rows in `rows` are already filtered by the host. */
  assignee?: AssigneeFilter;
  /** Shown instead of the usual empty line when the "Assigned to" filter is on and leaves no request (it names the filter and offers to clear it). */
  emptyFilterNote?: ReactNode;
  density: Density;
  /** The Caller summary (`GET /work/me`); without it no action is offered. */
  me: IssuedMe | null;
  /** After an ACCEPTED issuer or task action: the host reloads the lists the change touched. */
  onActionDone?: () => void;
  /** The ids of the tasks on the viewer's own oversight list: a task on it is in the viewer's scope, so Reassign is offered exactly as in Company / Team. Absent: only the company holder and the owner get controls. */
  oversightTaskIds?: ReadonlySet<string>;
  /** Edit task and Hand off open the host's existing form and dialog (the same ones My Tasks uses). Absent: those two entries are not offered here. */
  onTaskEntry?: (kind: TaskEntryKind, target: { taskId: string; requestId: string; title: string }) => void;
  /** Pass F1 (2026-10-25): the Status choice now applied by the host. For an Everyone request kept by an individual status or Overdue, the row names the recipients that matched. */
  statusChoice?: IssuedStatusChoice;
}

interface SetParts {
  trigger: ReactNode;
  panel: ReactNode;
  /** The recipients the Caller may read, opened from the chevron in the Assigned to cell (null while it is closed). */
  recipients: ReactNode;
  /** The chevron button of the Assigned to cell that opens and closes `recipients` (null when the request has none to show). */
  toggle: ReactNode;
  /** The small "Due changed" indicator of the Due cell when a readable recipient's due date was changed; it opens the recipients, where each change is listed against ITS recipient. Null otherwise. */
  dueExtra: ReactNode;
}

/** A recipient's Task due date as the forms write it. */
const dueText = (iso: string): string => formatDueDate(iso) ?? iso;

function SetActions({ row, me, issued, onActionDone, assignee, onTaskEntry, children }: { row: IssuedRow; me: IssuedMe | null; issued: IssuedStore; onActionDone?: () => void; assignee: AssigneeFilter; onTaskEntry?: IssuedRequestsPanelProps['onTaskEntry']; children: (parts: SetParts) => ReactNode }) {
  const [state, setState] = useState<IssuerFlowState>(initialIssuerFlow());
  // Increment 1a: whether the recipients of this Everyone request are open (the chevron of the Assigned to cell). Closed by default; opening it starts no action.
  const [expanded, setExpanded] = useState(false);
  // Section A: the History of ONE recipient's task (an individual-child action). Cancel request and Add recipient are set-level; neither is applied to a single child.
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const latest = useRef<IssuerFlowState>(state);
  const store = useRef<IssuerStore>({
    get: () => latest.current,
    set: (next) => {
      latest.current = next;
      setState(next);
    },
  }).current;
  const onDoneRef = useRef(onActionDone);
  onDoneRef.current = onActionDone;

  useEffect(() => onCallerChanged(() => store.set(callerChangedIssuer())), [store]);

  const controls = issuerControls(me, { requesterEmployeeId: row.requester?.employeeId ?? null, requestState: row.requestState, members: row.members });
  const flow = describeIssuerFlow(state, { takenEmployeeIds: row.members.map((m) => m.employeeId) });
  // Supplement 2026-10-24: the Edit form of one recipient's task can ask this request to open its own "Add recipient" flow. It opens only when the existing rule offers it (controls.add).
  const askedAdd = useAssignmentRequest(row.requestId);
  const handledAdd = useRef(0);
  useEffect(() => {
    if (askedAdd === null || askedAdd.control !== 'add-recipient' || askedAdd.nonce === handledAdd.current) return;
    handledAdd.current = askedAdd.nonce;
    if (controls.add) {
      store.set(openAdd(latest.current));
      void loadRecipients(workApi, store);
    }
  }, [askedAdd]); // eslint-disable-line react-hooks/exhaustive-deps
  if (row.status !== 'ready') return <>{children({ trigger: null, panel: null, recipients: null, toggle: null, dueExtra: null })}</>;

  async function confirm(): Promise<void> {
    const outcome = await performIssuerAction(workApi, store, { requestId: row.requestId });
    if (outcome.kind === 'done') {
      notifySharedTasksChanged({ reason: `set-${outcome.request.kind}`, requestId: row.requestId });
      await issued.applyChange(row.requestId, outcome.response);
      if (onDoneRef.current) onDoneRef.current();
    }
  }

  const entries: MenuEntry[] = [];
  if (controls.add) {
    entries.push({
      key: 'add',
      label: 'Add recipient…',
      ariaLabel: `Add a recipient to ${row.displayTitle}`,
      onSelect: () => {
        store.set(openAdd(latest.current));
        void loadRecipients(workApi, store);
      },
    });
  }
  // Owner direction 2026-10-23: an Everyone request has no edit of its own (the server has no set-level edit and each recipient's task is edited only by that person). The entry says so instead of being absent.
  if (controls.add || controls.cancel) entries.push({ key: 'edit', label: 'Edit task', ariaLabel: `Edit task, not available: ${row.displayTitle}. ${EDIT_EVERYONE_HINT}`, onSelect: () => undefined, disabled: true, hint: EDIT_EVERYONE_HINT });
  if (controls.cancel) entries.push({ key: 'cancel', label: 'Cancel request…', ariaLabel: `Cancel the request ${row.displayTitle}`, danger: true, onSelect: () => store.set(askCancel(latest.current)) });
  const trigger: ReactNode = !flow.open && entries.length > 0 ? <RowMoreMenu entries={entries} taskTitle={row.displayTitle} /> : null;
  // The readable recipients: First name + last initial; where two would look alike the full name is added beside the short one (displayNames.disambiguatePeople).
  const memberChoices = disambiguatePeople(row.members.map((member) => ({ employeeId: member.employeeId, displayName: member.displayName })));
  // The circles of the recipients, labelled together so no two in this list look alike.
  const memberCircles = avatarLabels(row.members.map((member) => ({ id: member.employeeId, name: member.displayName })));
  // The people the issuer may add: the same labels, from the whole candidate list.
  const optionChoices = disambiguatePeople(flow.options);

  // Increment 1a: the recipients are named in the Assigned to cell; this is the expansion a chevron there opens. It lists exactly the recipients the existing list already showed
  // ("Recipients you can see"): each person, that child's status, and Remove where the Caller may remove them. Opening it starts no assignment action.
  const detail = recipientsDetailOf(row);
  const listId = `issued-recipients-${row.requestId}`;
  const toggle: ReactNode =
    detail === null ? null : <RecipientsToggle expanded={expanded} controls={listId} label={row.displayTitle} onToggle={() => setExpanded((open) => !open)} />;
  const recipients: ReactNode =
    detail !== null && expanded ? (
      <div id={listId} className="mt-1 text-xs" data-testid="issued-recipients">
        <p className="font-medium text-gray-700">{detail.heading}</p>
        <p className="text-gray-500">Remove and History apply to one recipient. Cancel request applies to the whole request.</p>
        <ul className="mt-1 space-y-0.5">
          {row.members.map((member, index) => (
            <li key={member.employeeId} className="flex flex-wrap items-center gap-2">
              <AvatarCircle badge={memberCircles[index]} size="compact" />
              <span className="text-gray-900">
                {/* A member with a Remove button has that button as the tab stop (its name carries the full name); the name is a stop of its own only where there is no button. */}
                <PersonName person={member.displayName} focusable={!(controls.removable.includes(member.employeeId) && isLiveAssignment(member))} />
                {memberChoices[index].detail !== '' && <span className="ml-1 text-gray-500">({memberChoices[index].detail})</span>}
              </span>
              {/* Increment 1b-ii: with an Assigned to filter on, the recipient it matched is marked IN WORDS (a border and text, never colour alone). Only a live recipient can match (an ended one is not in
                  the Assigned to cell), and with no filter nobody is marked. The request is whole: every other recipient is listed as before. */}
              {!member.ended && isAssigneeMatch(assignee, member.employeeId) && (
                <span className="rounded border border-sky-300 bg-sky-50 px-1.5 py-px text-[11px] font-medium text-sky-900" data-testid="assignee-match-marker">
                  {ASSIGNEE_MATCH_MARKER}
                </span>
              )}
              <span className={member.ended ? 'text-amber-800' : 'text-gray-600'}>{detail.recipients[index].statusText}</span>
              {/* Owner direction 2026-10-23: each live recipient's OWN priority and Task due date, so an Everyone row that says "Varies" can be read here. */}
              {detail.recipients[index].priorityText !== null && (
                <span className="text-gray-600" data-testid="recipient-own-values">
                  {detail.recipients[index].priorityText}
                  {', '}
                  {detail.recipients[index].dueDate === '' ? 'no due date' : `due ${dueText(detail.recipients[index].dueDate ?? '')}`}
                </span>
              )}
              {me !== null && member.itemId !== '' && (
                <button
                  type="button"
                  onClick={() => setHistoryFor((current) => (current === member.itemId ? null : member.itemId))}
                  aria-expanded={historyFor === member.itemId}
                  aria-label={`History: ${fullName(member.displayName)}, ${row.displayTitle}`}
                  className="underline text-[#1f6a99]"
                >
                  History
                </button>
              )}
              {/* Owner decision D8: the requester edits ONE recipient's task (title, description, priority, due date) from here. It changes that person's task only: no other recipient, and not the request as a whole. */}
              {onTaskEntry !== undefined && me !== null && member.itemId !== '' && !member.ended && !member.restricted && member.status !== null && member.status !== 'completed' && (
                <button
                  type="button"
                  onClick={() => onTaskEntry('edit', { taskId: member.itemId, requestId: row.requestId, title: row.displayTitle })}
                  aria-label={`Edit task for ${fullName(member.displayName)}: ${row.displayTitle}`}
                  data-testid="recipient-edit-task"
                  className="underline text-[#1f6a99]"
                >
                  Edit task
                </button>
              )}
              <MemberInsight member={member} />
              {controls.removable.includes(member.employeeId) && isLiveAssignment(member) && (
                <button
                  type="button"
                  disabled={flow.sending}
                  onClick={() => store.set(askRemove(latest.current, member.employeeId, member.displayName))}
                  aria-label={`Remove ${fullName(member.displayName)} from ${row.displayTitle}`}
                  title={`Remove ${fullName(member.displayName)}`}
                  className="underline text-red-700 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
        {historyFor !== null && <TaskHistoryPanel taskId={historyFor} title={`${row.displayTitle} (one recipient)`} onClose={() => setHistoryFor(null)} />}
        {/* A recipient whose task is restricted shows neither figure, so the expansion says so once instead of showing a made-up one. */}
        {!detail.dueAvailable && <p className="mt-1 text-gray-500">Priority and due date are not shown for a restricted task.</p>}
      </div>
    ) : null;

  const panel: ReactNode =
    flow.outcome === null && !flow.open ? null : (
      <div className="mt-1 w-full space-y-1" data-testid="issuer-actions">
        {flow.outcome !== null && (
          <p role="status" aria-live="polite" className="flex items-start justify-between gap-2 text-xs bg-green-50 text-green-900 border border-green-200 rounded px-2 py-1">
            <span>{flow.outcome}</span>
            <button type="button" onClick={() => store.set(dismissIssuerOutcome(latest.current))} aria-label="Dismiss message" className="font-bold">
              &times;
            </button>
          </p>
        )}

        {flow.open && (
          <div role="group" aria-label={`Change the request ${row.displayTitle}`} className="border border-violet-200 bg-violet-50 rounded p-2 text-xs space-y-2">
            {flow.prompt !== null && <p className="text-gray-800">{flow.prompt}</p>}

            {(state.phase === 'adding' || (state.phase === 'sending' && state.from === 'adding')) && (
              <div>
                {flow.candidates.phase === 'loading' && (
                  <p role="status" className="text-gray-600">
                    Loading people…
                  </p>
                )}
                {flow.candidates.phase === 'error' && (
                  <p role="alert" className="text-red-700">
                    Could not load people{flow.candidates.error !== null && flow.candidates.error.message !== '' ? `: ${flow.candidates.error.message}` : ''}.{' '}
                    <button type="button" onClick={() => void loadRecipients(workApi, store)} className="underline" aria-label="Retry loading people">
                      Retry
                    </button>
                  </p>
                )}
                {flow.candidates.phase === 'data' && flow.options.length === 0 && <p className="text-gray-600">Everyone you can choose is already in this request.</p>}
                {flow.candidates.phase === 'data' && flow.options.length > 0 && (
                  <>
                    <label htmlFor={`add-${row.requestId}`} className="block text-gray-700 mb-1">
                      Who should be added?
                    </label>
                    <select
                      id={`add-${row.requestId}`}
                      value={flow.selected}
                      disabled={flow.sending}
                      onChange={(event) => store.set(selectRecipient(latest.current, event.target.value))}
                      className="w-full max-w-xs px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                    >
                      <option value="">Choose a person…</option>
                      {flow.options.map((person, index) => (
                        <option key={person.employeeId} value={person.employeeId} title={optionChoices[index].accessibleName}>
                          {optionChoices[index].detail !== '' ? `${optionChoices[index].short} (${optionChoices[index].detail})` : optionChoices[index].short}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}

            {flow.failure !== null && (
              <p role="alert" className="text-red-700">
                <strong>{flow.failure.headline}</strong>
                {flow.failure.detail !== null && <span className="ml-1">{flow.failure.detail}</span>}
              </p>
            )}

            <div className="flex items-center gap-2">
              <button type="button" disabled={flow.confirm.disabled} onClick={() => void confirm()} className="px-2 py-0.5 rounded bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:opacity-50">
                {flow.confirm.label}
              </button>
              <button type="button" disabled={flow.sending} onClick={() => store.set(closeIssuer(latest.current))} className="px-2 py-0.5 rounded border border-gray-300 bg-white disabled:opacity-50">
                Keep the request
              </button>
            </div>
          </div>
        )}
      </div>
    );

  const memberChanges = row.members.reduce((sum, member) => sum + (member.insight === null ? 0 : member.insight.dueChanges.length), 0);
  const dueExtra: ReactNode =
    memberChanges > 0 && detail !== null ? <DueChangedButton count={memberChanges} open={expanded} controls={listId} onToggle={() => setExpanded((open) => !open)} /> : null;
  return <>{children({ trigger, panel, recipients, toggle, dueExtra })}</>;
}

export function IssuedRequestsPanel({ issued, view, rows, narrowed, assignee = ANY_ASSIGNEE, emptyFilterNote, density, me, onActionDone, oversightTaskIds, onTaskEntry, statusChoice }: IssuedRequestsPanelProps) {
  // The list comes from the server (GET /work/issued), so it is read when the subview is shown; it survives a reload of the page.
  useEffect(() => {
    void issued.ensureLoaded();
  }, [issued]);

  return (
    <section aria-labelledby="requests-issued-heading" data-testid="requests-issued">
      {/* Layout-1: the tab already says "Assigned by Me"; the heading stays for assistive technology only. Its Refresh moved to the right end of the toolbar row (MyWorkPage issuedRefresh). */}
      <h2 id="requests-issued-heading" className="sr-only">
        Assigned by Me: the requests you issued
      </h2>
      {view.status === 'unavailable' && (
        <p role="status" className="text-xs text-gray-600" data-testid="requests-issued-unavailable">
          {view.message}
        </p>
      )}
      {view.status === 'error' && (
        <p role="alert" className="text-xs text-red-700">
          {view.message}{' '}
          <button type="button" onClick={() => void issued.load()} className="underline" aria-label="Retry loading requests I issued">
            Retry
          </button>
        </p>
      )}
      {view.status === 'loading' && view.rows.length === 0 && (
        <p role="status" className="text-xs text-gray-500">
          Loading requests I issued…
        </p>
      )}
      {view.rows.length === 0 ? (
        view.status === 'ready' && <p className="text-xs text-gray-500">{view.emptyText}</p>
      ) : rows.length === 0 ? (
        // A chosen person who leaves no request: the empty state names the "Assigned to" filter and offers to clear it. Otherwise the search is what narrowed the list.
        // Pass F1: while more pages remain, an empty result only says nothing LOADED matches yet, and Load more (below) is the way to keep looking.
        emptyFilterNote ?? <p className="text-xs text-gray-500" data-testid="issued-narrowed-empty">{narrowed ? (view.canLoadMore ? `No loaded request matches yet (${view.loadedCount} loaded). Load more to keep looking.` : 'No requests match the search.') : view.emptyText}</p>
      ) : (
        <TaskTable variant="issued" ariaLabel="Assigned by Me: requests you assigned to other people">
          {rows.map((row) => (
            <SetOrTaskRow key={row.requestId} row={row} me={me} issued={issued} density={density} assignee={assignee} onActionDone={onActionDone} oversightTaskIds={oversightTaskIds} onTaskEntry={onTaskEntry} statusChoice={statusChoice} />
          ))}
        </TaskTable>
      )}
      {view.loadMoreError !== null && (
        <p role="alert" className="mt-2 text-xs text-red-700">
          More requests could not be loaded: {view.loadMoreError}
        </p>
      )}
      {(view.canLoadMore || view.loadingMore) && (
        <button
          type="button"
          disabled={view.loadingMore}
          onClick={() => void issued.loadMore()}
          className="mt-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
        >
          {view.loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );
}

interface EntryProps {
  row: IssuedRow;
  me: IssuedMe | null;
  issued: IssuedStore;
  density: Density;
  assignee: AssigneeFilter;
  onActionDone?: () => void;
  oversightTaskIds?: ReadonlySet<string>;
  onTaskEntry?: IssuedRequestsPanelProps['onTaskEntry'];
  statusChoice?: IssuedStatusChoice;
}

/** Pass F1: "Waiting: Alice R., Ben S." under an Everyone request that an individual status or Overdue kept; the request stays whole and its other recipients are in the expansion. */
function StatusMatchNote({ names, choice }: { names: readonly string[]; choice: IssuedStatusChoice }) {
  return (
    <p className="text-xs text-gray-700" data-testid="status-match">
      <span className="font-medium">{statusLabelOf(ISSUED_STATUS_OPTIONS, choice)}:</span>{' '}
      {names.map((name, index) => (
        <span key={`${name}-${index}`}>
          {index > 0 ? ', ' : ''}
          <PersonName person={name} focusable={false} />
        </span>
      ))}
    </p>
  );
}

/** The parts of an entry that do not depend on which kind of request it is. */
function entryFrame(
  row: IssuedRow,
  density: Density,
  parts: {
    trigger: ReactNode;
    below: ReactNode;
    toggle?: ReactNode;
    /** Comfortable: the labelled summary of the recorded work (latest progress, next action, follow-up) under the title. */
    detail?: ReactNode;
    /** Beside the title in both densities: the small "Details" toggle that reaches the same lines (Compact's way to them). */
    titleExtra?: ReactNode;
    /** In the Due cell: the small "Due changed" indicator. */
    dueExtra?: ReactNode;
    /** BUILD-046 G4: where a follow-up that is due stands, and for whom (an Everyone request names each recipient). Beside the title in both densities. */
    followUp?: IssuedFollowUpNote | null;
  },
  issued: IssuedStore,
  // CHANGED 2026-10-25: read by the row COMPONENT (useCustomersInPlay) and handed in, because this function is also called from render-prop children where a hook does not belong.
  customersInPlay: boolean
) {
  const columns = describeIssuedColumns(row);
  const compact = density === 'compact';
  // Compact is ONE tight row: the owner-left text (the server's, exactly as given: Req 4.28) and the request's sentence are inline, cut with an ellipsis (the full text is the hover
  // text), never a second line. Comfortable keeps them as quiet lines under the row.
  const ownerLeftText = row.ownerLeft.join(' ');
  const below =
    (!compact && row.ownerLeft.length > 0) || (parts.below !== null && parts.below !== undefined) ? (
      <>
        {!compact && row.ownerLeft.length > 0 && (
          <ul className="mt-1 text-xs text-amber-800 space-y-0.5" data-testid="issued-owner-left">
            {row.ownerLeft.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        {parts.below}
      </>
    ) : undefined;
  return (
    <TaskTableRow variant="issued" id={`issued-${row.requestId}`} testId="issued-row" density={density} job={row.job} customer={row.customer} below={below}>
      {/* Task: the strong title with the small mode badge beside it; Comfortable adds the quiet Customer and Customer / Job line and only useful secondary words (priority, due, who and when have columns). */}
      <TaskCell
        title={row.title}
        density={density}
        titleSuffix={
          <>
            <ModeBadge badge={columns.mode} />
            {parts.titleExtra}
          </>
        }
        secondary={
          <QuietLine
            parts={[
              // BUILD-046 J1a: the Customer, then the Customer / Job, are the FIRST parts in Comfortable; Compact has its own column. A job beside a customer is written "Job: <name>".
              ...associationParts(row, density, { customersInPlay }),
              parts.followUp !== undefined && parts.followUp !== null ? (
                <span key="follow-up" className="font-medium text-amber-800" title={parts.followUp.title} data-testid="issued-follow-up">
                  {parts.followUp.text}
                </span>
              ) : null,
              row.sessionOnly ? 'Just issued' : null,
              row.reassignmentNeeded ? 'Reassignment needed' : null,
              compact && ownerLeftText !== '' ? (
                <span key="owner-left" className="text-amber-800" title={ownerLeftText} data-testid="issued-owner-left">
                  {ownerLeftText}
                </span>
              ) : null,
              compact ? row.wording : null,
            ]}
          />
        }
        detail={
          !compact && (row.wording !== null || (parts.detail !== undefined && parts.detail !== null)) ? (
            <div className="space-y-0.5">
              {row.wording !== null && <span className="block" data-testid="issued-wording">{row.wording}</span>}
              {parts.detail}
            </div>
          ) : undefined
        }
      />
      <Cell stackedLabel="Assigned to">
        <ResponsibleView cell={columns.responsible} density={density} toggle={parts.toggle} />
      </Cell>
      <Cell stackedLabel="Status">
        <ProgressView cell={columns.progress} density={density} />
        {row.canRetry && (
          <button type="button" onClick={() => void issued.refresh(row.requestId)} aria-label={`Retry the figure for ${row.displayTitle}`} className="ml-2 underline text-red-700">
            Retry
          </button>
        )}
      </Cell>
      <Cell stackedLabel="Priority">
        <PriorityView cell={columns.priority} />
      </Cell>
      <Cell stackedLabel="Due">
        <DueView data={columns.due} stack={!compact} />
        {parts.dueExtra}
      </Cell>
      <Cell stackedLabel="Assigned">
        <AssignedAgeView createdAt={columns.createdAt} density={density} />
      </Cell>
      <ActionsCell>{parts.trigger}</ActionsCell>
    </TaskTableRow>
  );
}

function SetOrTaskRow({ row, me, issued, density, assignee, onActionDone, oversightTaskIds, onTaskEntry, statusChoice }: EntryProps) {
  const today = usePhoenixDay();
  const customersInPlay = useCustomersInPlay();
  const matched = statusChoice === undefined ? [] : matchingMemberNames(row, statusChoice, today);
  const followUp = issuedFollowUpNote(row, today, (date) => formatDueDate(date) ?? date);
  if (row.kind === 'set') {
    return (
      <SetActions row={row} me={me} issued={issued} onActionDone={onActionDone} assignee={assignee} onTaskEntry={onTaskEntry}>
        {({ trigger, panel, recipients, toggle, dueExtra }) =>
          entryFrame(
            row,
            density,
            {
              trigger,
              toggle,
              dueExtra,
              followUp,
              detail: statusChoice !== undefined && matched.length > 0 ? <StatusMatchNote names={matched} choice={statusChoice} /> : undefined,
              below:
                recipients !== null || panel !== null ? (
                  <>
                    {recipients}
                    {panel}
                  </>
                ) : null,
            },
            issued,
            customersInPlay
          )
        }
      </SetActions>
    );
  }
  if ((row.kind === 'queue' || row.kind === 'direct') && row.task !== null) {
    return <IssuedTaskRow row={row} task={row.task} me={me} issued={issued} density={density} assignee={assignee} oversightTaskIds={oversightTaskIds} onTaskEntry={onTaskEntry} onActionDone={onActionDone} />;
  }
  return entryFrame(row, density, { trigger: null, below: null }, issued, customersInPlay);
}

/**
 * A Single or queue request (one task). The actions are the existing ones, offered by the existing rules: Reassign, Assign to, Recovery assignment, Return to queue and Reopen from TaskActionControls
 * (oversightActionsModel), and Edit task, Hand off and History from taskActionOffers.ts. A task on the viewer's own oversight list is in the viewer's scope exactly as it is in Company / Team; the requester role
 * alone adds nothing, and every request is still decided by the server (a refusal is shown in its words and changes nothing).
 */
function IssuedTaskRow({ row, task, me, issued, density, oversightTaskIds, onTaskEntry, onActionDone }: EntryProps & { task: IssuedTask }) {
  const today = usePhoenixDay();
  const customersInPlay = useCustomersInPlay();
  const [historyOpen, setHistoryOpen] = useState(false);
  // Owner correction 2026-10-24 (item 2): the recorded work on this task, for the person who issued it. The summary is under the title in Comfortable; the "Details" toggle (always in Compact, and in
  // Comfortable when the note is long) opens the whole of it; "Due changed" opens each recorded change. A task shown in its restricted form has none of this.
  const [detailOpen, setDetailOpen] = useState(false);
  const [dueOpen, setDueOpen] = useState(false);
  // True while the one-line progress summary is cut off by the width of the window (a narrow screen): the "Details" toggle is then offered in Comfortable too, so the whole note stays reachable.
  const [clipped, setClipped] = useState(false);
  const progress = task.redacted || task.insight === null ? null : task.insight.progress;
  const dueChanges = task.redacted || task.insight === null ? [] : task.insight.dueChanges;
  const hasLines = !task.redacted && hasInsightLines(progress, task.nextAction, task.followUpDate);
  const longNote = progress !== null && progress.text.length > 90;
  const compactRow = density === 'compact';
  const detailsId = `insight-${task.id}`;
  const dueId = `due-${task.id}-changes`;
  const target = { taskId: task.id, requestId: row.requestId, title: row.displayTitle };
  const entries = taskEntries(taskEntryOffers(me, task), row.displayTitle, {
    onUpdate: () => onTaskEntry?.('update', target),
    onEdit: () => onTaskEntry?.('edit', target),
    onHandoff: () => onTaskEntry?.('handoff', target),
    onHistory: () => setHistoryOpen((open) => !open),
  }, { editHint: editBlockedHint(me, task) }).filter((entry) => entry.key === 'history' || entry.disabled === true || onTaskEntry !== undefined);
  const inScope = oversightTaskIds !== undefined && oversightTaskIds.has(task.id);
  return (
    <TaskActionControls
      me={me}
      row={{ ...task, inOversightScope: inScope }}
      view="issued"
      onDone={() => {
        void issued.refresh(row.requestId);
        if (onActionDone) onActionDone();
      }}
      extraEntries={entries}
    >
      {({ trigger, panel }) =>
        entryFrame(
          row,
          density,
          {
            trigger,
            followUp: issuedFollowUpNote(row, today, (date) => formatDueDate(date) ?? date),
            detail: hasLines ? <InsightLines progress={progress} nextAction={task.nextAction} followUpDate={task.followUpDate} onClipped={setClipped} /> : null,
            titleExtra:
              hasLines && (compactRow || longNote || clipped) ? (
                <button
                  type="button"
                  aria-expanded={detailOpen}
                  aria-controls={detailsId}
                  onClick={() => setDetailOpen((open) => !open)}
                  data-testid="insight-toggle"
                  title="The latest progress, next action and follow-up of this task"
                  className="rounded border border-gray-300 bg-white px-1.5 py-px text-[11px] font-medium text-[#1f6a99] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
                >
                  {detailOpen ? 'Hide details' : 'Details'}
                </button>
              ) : null,
            dueExtra:
              dueChanges.length > 0 ? <DueChangedButton count={dueChanges.length} open={dueOpen} controls={dueId} onToggle={() => setDueOpen((open) => !open)} /> : null,
            below:
              historyOpen || panel !== null || (detailOpen && hasLines) || (dueOpen && dueChanges.length > 0) ? (
                <>
                  {detailOpen && hasLines && (
                    <div id={detailsId} className="mt-1 w-full rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-900">
                      <InsightLines progress={progress} nextAction={task.nextAction} followUpDate={task.followUpDate} full />
                    </div>
                  )}
                  {dueOpen && dueChanges.length > 0 && <DueChangesPanel id={dueId} changes={dueChanges} incomplete={task.insight !== null && task.insight.incomplete} onClose={() => setDueOpen(false)} />}
                  {historyOpen && <TaskHistoryPanel taskId={task.id} title={row.displayTitle} onClose={() => setHistoryOpen(false)} />}
                  {panel}
                </>
              ) : null,
          },
          issued,
          customersInPlay
        )
      }
    </TaskActionControls>
  );
}
